package dev.lingvo.ops.pages.workspaces

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.pages.OpsResourcePagination
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.components.OpsWorkspaceNav
import dev.lingvo.ops.state.auditFilterSummary
import dev.lingvo.ops.state.rememberOpsPaging
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.shared.AuditEventDto
import dev.lingvo.shared.ops.OpsPage
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LCodeLanguage
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.nav.UrlState
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private data class AuditQuery(val orgId: String, val actor: String, val action: String, val cursor: String?)

@Page("/workspaces/{orgId}/audit")
@Composable
fun AuditPage() {
    val ctx = rememberPageContext()
    val orgId = ctx.route.params["orgId"].orEmpty()
    var actor by remember(orgId) { mutableStateOf(UrlState.read(ctx, "actor").orEmpty()) }
    var action by remember(orgId) { mutableStateOf(UrlState.read(ctx, "action").orEmpty()) }
    var seedCursor by remember(orgId) { mutableStateOf(UrlState.read(ctx, "after")) }
    OpsShell("Workspaces") {
        val paging = rememberOpsPaging<AuditEventDto>(listOf(orgId, actor, action), pageSize = 50, deepLink = seedCursor)
        val resource = rememberOpsResource(AuditQuery(orgId, actor, action, paging.cursor)) { query ->
            OpsApi.orgAudit(query.orgId, query.actor.ifBlank { null }, query.action.ifBlank { null }, query.cursor)
        }
        LaunchedEffect(actor, action, paging.cursor) {
            UrlState.replace(mapOf("actor" to actor, "action" to action, "after" to paging.cursor))
        }
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            OpsPageHeader("Workspace audit", "Read customer-visible events and the recorded detail behind each sentence.", resource.state.isRefreshing, resource::refresh)
            OpsWorkspaceNav(orgId, "Audit")
            LFilterBar(
                fields = listOf(
                    LFilterField("audit-actor", "Actor", actor.ifBlank { null }, { actor = ""; seedCursor = null }) { modifier ->
                        LSearchField(actor, { actor = it; seedCursor = null }, modifier, placeholder = "Actor kind or label", shortcutHint = null)
                    },
                    LFilterField("audit-action", "Action", action.ifBlank { null }, { action = ""; seedCursor = null }) { modifier ->
                        LSearchField(action, { action = it; seedCursor = null }, modifier, placeholder = "Action", shortcutHint = null)
                    },
                ),
                onClearAll = { actor = ""; action = ""; seedCursor = null },
            )
            OpsPanel("Timeline", "Events follow the server's retention window; this page is not a complete lifetime history.") {
                OpsResourceStates(resource.state, "No audit events in this page", "No events were returned for this workspace.",
                    filtered = "No event matches these filters".takeIf { actor.isNotBlank() || action.isNotBlank() }, retry = resource::refresh) { value ->
                    P { Text("${auditFilterSummary(actor, action, value.events.size)} Retention: ${value.retentionDays} days.") }
                    value.events.forEach { event ->
                        P { Text("${formatUtc(event.createdAt)} · ${event.actorKind.name} · ${event.sentence}") }
                        if (event.payload.isNotEmpty()) {
                            LCodeBlock(JsonObject(event.payload.mapValues { JsonPrimitive(it.value) }).toString(), "Recorded detail", language = LCodeLanguage.JSON, maxLines = 8)
                        }
                    }
                }
                OpsResourcePagination(paging, resource.state, "events") { value -> OpsPage(value.events, value.nextCursor, 50) }
            }
        }
    }
}
